/* =========================================================================
 * LegalDoc — shared layout for the legal pages (EULA, app terms, privacy).
 *
 * Matches the rest of the 2026 redesign: DefaultNavbar + SiteFooter, hero
 * with eyebrow + title + last-updated, numbered section list with brand-
 * aligned typography. Each page declares its sections as plain data and
 * lets this component handle layout + chrome.
 * ========================================================================= */

import PropTypes from "prop-types";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import SiteFooter from "components/SiteFooter";

import consumerRoutes from "consumerRoutes";
import footerRoutes from "footer.routes";

export function LegalDoc({ eyebrow, title, lastUpdated, lead, sections, footnote }) {
  return (
    <>
      <DefaultNavbar routes={consumerRoutes} sticky />

      {/* Hero */}
      <MKBox
        component="section"
        sx={({ palette }) => ({
          background: `linear-gradient(180deg, ${palette.white.main} 0%, #EFF6FF 100%)`,
          pt: { xs: 12, md: 16 },
          pb: { xs: 6, md: 8 },
        })}
      >
        <Container>
          <Grid container justifyContent="center" textAlign="center">
            <Grid item xs={12} md={10} lg={9}>
              <MKTypography
                variant="overline"
                color="primary"
                fontWeight="bold"
                sx={{ letterSpacing: "0.1em" }}
              >
                {eyebrow}
              </MKTypography>
              <MKTypography
                variant="h1"
                color="dark"
                mt={1.5}
                sx={({ breakpoints, typography: { size } }) => ({
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  [breakpoints.down("md")]: { fontSize: size["3xl"] },
                })}
              >
                {title}
              </MKTypography>
              {lastUpdated && (
                <MKTypography
                  variant="caption"
                  color="text"
                  mt={2}
                  sx={{ display: "block", fontSize: 13, opacity: 0.7 }}
                >
                  Last updated · {lastUpdated}
                </MKTypography>
              )}
              {lead && (
                <MKTypography
                  variant="body1"
                  color="text"
                  mt={3}
                  sx={{ maxWidth: 720, mx: "auto", fontSize: { xs: 16, md: 18 }, lineHeight: 1.6 }}
                >
                  {lead}
                </MKTypography>
              )}
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      {/* Sections */}
      <MKBox component="section" py={{ xs: 6, md: 10 }} px={{ xs: 2, md: 4 }}>
        <Container maxWidth="md">
          {sections.map((s, i) => (
            <MKBox
              key={s.title}
              sx={({ palette }) => ({
                py: { xs: 3.5, md: 4.5 },
                borderTop: i === 0 ? "none" : `1px solid ${palette.grey[200]}`,
              })}
            >
              <Grid container spacing={{ xs: 2, md: 4 }} alignItems="flex-start">
                <Grid item xs={12} md={3}>
                  <MKTypography
                    variant="overline"
                    color="primary"
                    fontWeight="bold"
                    sx={{ letterSpacing: "0.1em", display: "block", lineHeight: 1 }}
                  >
                    Section {String(i + 1).padStart(2, "0")}
                  </MKTypography>
                  <MKTypography
                    variant="h5"
                    color="dark"
                    fontWeight="bold"
                    sx={{ mt: 1, letterSpacing: "-0.01em" }}
                  >
                    {s.title}
                  </MKTypography>
                </Grid>
                <Grid item xs={12} md={9}>
                  <MKBox
                    sx={({ palette }) => ({
                      color: palette.text.main,
                      fontSize: 15,
                      lineHeight: 1.7,
                      "& p": { m: 0, mb: 2 },
                      "& p:last-child": { mb: 0 },
                      "& ul": { pl: 2.5, my: 1.5 },
                      "& li": { mb: 1, lineHeight: 1.65 },
                      "& li:last-child": { mb: 0 },
                      "& a": {
                        color: palette.primary.main,
                        textDecoration: "underline",
                        fontWeight: 600,
                      },
                      "& strong, & b": { color: palette.dark.main, fontWeight: 600 },
                    })}
                  >
                    {s.body}
                  </MKBox>
                </Grid>
              </Grid>
            </MKBox>
          ))}
        </Container>
      </MKBox>

      {footnote && (
        <MKBox
          component="section"
          py={{ xs: 5, md: 7 }}
          px={{ xs: 2, md: 4 }}
          sx={({ palette }) => ({
            background: palette.grey[100],
            borderTop: `1px solid ${palette.grey[200]}`,
          })}
        >
          <Container maxWidth="md">
            <MKTypography
              variant="body2"
              color="text"
              sx={({ palette }) => ({
                lineHeight: 1.65,
                "& a": {
                  color: palette.primary.main,
                  textDecoration: "underline",
                  fontWeight: 600,
                },
              })}
            >
              {footnote}
            </MKTypography>
          </Container>
        </MKBox>
      )}

      <SiteFooter content={footerRoutes} />
    </>
  );
}

LegalDoc.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string.isRequired,
  lastUpdated: PropTypes.string,
  lead: PropTypes.node,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      body: PropTypes.node.isRequired,
    })
  ).isRequired,
  footnote: PropTypes.node,
};

LegalDoc.defaultProps = {
  eyebrow: "Legal",
  lastUpdated: null,
  lead: null,
  footnote: null,
};
